import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useColors, useResponsive } from "@inbody/core";
import { useAuth, useCorporate, useKeycloakMutations } from "@inbody/keycloak";
import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
const CORPORATE_MENU_MIN_WIDTH = 220;
function CorporateChangeButton({
  compact = true
} = {}) {
  const { t } = useTranslation("ui");
  const { gray, brand } = useColors();
  const { isMobile } = useResponsive();
  const { corporateCodeList, selectedCorporateCode } = useAuth();
  const { getCorporateCodeLabel } = useCorporate();
  const { useUpdateSelectedCorporateCode } = useKeycloakMutations();
  const { mutate: updateCorporateCode, isPending } = useUpdateSelectedCorporateCode();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const handleChange = useCallback(
    (corporateCode) => {
      setAnchorEl(null);
      updateCorporateCode(corporateCode);
    },
    [updateCorporateCode]
  );
  if (!corporateCodeList || corporateCodeList.length <= 1) {
    return null;
  }
  const selectedLabel = getCorporateCodeLabel(
    selectedCorporateCode
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      ButtonBase,
      {
        "aria-haspopup": "true",
        "aria-expanded": open ? "true" : void 0,
        onClick: handleClick,
        disabled: isPending,
        sx: {
          borderRadius: 1.5,
          px: isMobile ? 0.75 : 1.25,
          py: 0.75,
          color: gray[700],
          "&:hover": {
            backgroundColor: gray[50],
            color: brand[500]
          }
        },
        children: /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 0.75, alignItems: "center", children: [
          /* @__PURE__ */ jsx(
            BusinessIcon,
            {
              sx: {
                fontSize: compact ? "24px !important" : 18
              }
            }
          ),
          !isMobile && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              Typography,
              {
                variant: compact ? "caption" : "body2",
                sx: { fontWeight: 500 },
                children: selectedLabel
              }
            ),
            /* @__PURE__ */ jsx(
              KeyboardArrowDownIcon,
              {
                sx: {
                  fontSize: compact ? "24px !important" : 16
                }
              }
            )
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      Popover,
      {
        anchorEl,
        open,
        onClose: handleClose,
        transformOrigin: { horizontal: "right", vertical: "top" },
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
        slotProps: {
          paper: {
            sx: {
              borderRadius: 2,
              border: `1px solid ${gray[100]}`,
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              minWidth: CORPORATE_MENU_MIN_WIDTH,
              mt: 1,
              overflow: "hidden"
            }
          }
        },
        children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              sx: {
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${gray[100]}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              },
              children: [
                /* @__PURE__ */ jsx(
                  Typography,
                  {
                    variant: "body2",
                    sx: { fontWeight: 700, color: gray[800] },
                    children: t("기업 선택")
                  }
                ),
                /* @__PURE__ */ jsx(Typography, { variant: "small", sx: { color: gray[400] }, children: selectedLabel })
              ]
            }
          ),
          /* @__PURE__ */ jsx(Box, { sx: { py: 0.5 }, children: corporateCodeList.map((corporate) => {
            const selected = selectedCorporateCode === corporate.value;
            return /* @__PURE__ */ jsxs(
              MenuItem,
              {
                disabled: isPending,
                onClick: () => handleChange(corporate.value),
                sx: {
                  px: 2.5,
                  py: 1.25,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: selected ? brand[500] : gray[700],
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: gray[50],
                    color: brand[500]
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "body2",
                      sx: { fontWeight: selected ? 600 : 500 },
                      children: getCorporateCodeLabel(corporate.value)
                    }
                  ),
                  selected && /* @__PURE__ */ jsx(CheckIcon, { sx: { fontSize: 16 } })
                ]
              },
              corporate.value
            );
          }) })
        ]
      }
    )
  ] });
}
export {
  CorporateChangeButton
};
